import { useEffect, useRef, useState } from 'react';
import { useDevNetworkOverride } from '@/data/devNetworkOverride';

// Debounce delay to prevent state thrashing on flaky networks
const NETWORK_DEBOUNCE_MS = 300;

/**
 * Hook to monitor online/offline network status
 * Includes debouncing to prevent rapid state changes on unstable connections
 * @returns {object} Object containing isOnline boolean
 */
export function useNetworkStatus() {
  const { forceOffline } = useDevNetworkOverride();
  const [onlineStatus, setOnlineStatus] = useState(() => {
    // Initialize with current status
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true; // Default to online in SSR
  });

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOnline = forceOffline ? false : onlineStatus;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Debounced handler for when network goes online
    const handleOnline = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setOnlineStatus(true);
      }, NETWORK_DEBOUNCE_MS);
    };

    // Debounced handler for when network goes offline
    const handleOffline = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setOnlineStatus(false);
      }, NETWORK_DEBOUNCE_MS);
    };

    // Listen to online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup listeners and timer on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { isOnline };
}
